import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { AgendaMagasin } from '@components/agenda-magasin/models';
import { N_DISPLAYED_SLOTS } from '@components/agenda-magasin/config';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { LoaderComponent } from '@shared/components/loader';
import { NoSlotFallbackCardComponent } from '@shared/components/no-slot-fallback-card';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { StoreWithUuid } from '@shared/models';
import { TransformFirstLetterPipe } from '@shared/pipe';
import { LogsService } from '@shared/services/logs';
import { ResponsiveService } from '@shared/services/responsive';
import { TimeslotsService } from '@shared/services/timeslots';
import { FormattedSlots, Timeslots } from '@shared/services/timeslots/models';
import { formatPhone } from '@shared/utils';
import { CommonStore, FunnelStore } from '@stores';
import { EMPTY, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ElasticSearchService } from '@shared/services/elastic-search';

@Component({
  selector: 'app-agenda-magasin',
  imports: [
    TitleComponent,
    LoaderComponent,
    SvgComponent,
    TransformFirstLetterPipe,
    TranslatePipe,
    NgClass,
    NoSlotFallbackCardComponent,
    ButtonComponent,
    ImageComponent,
  ],
  templateUrl: './agenda-magasin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgendaMagasinComponent extends AbstractDynamicComponent<AgendaMagasin> implements OnInit {
  readonly timeslots = signal<Timeslots[]>(null!);
  readonly store = signal<Partial<StoreWithUuid>>(null!);
  readonly isTimeslotsError = signal(false);
  readonly slotClosed = signal<Set<string>>(new Set());
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  readonly visioAppointmentSelected = signal(false);
  private readonly displayCountMap = new Map<Timeslots, number>();
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly timeslotsService = inject(TimeslotsService);
  private readonly funnelStore = inject(FunnelStore);
  private readonly appointmentFunnel = inject(CommonStore).select('appointmentFunnel');
  private readonly router = inject(Router);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    effect(() => {
      const timeSlot = this.timeslots();
      if (timeSlot) {
        const slotClosed = this.slotClosed();
        timeSlot.forEach((slot) => {
          if (slot.formattedSlots.length === 0) {
            slotClosed.add(slot.dateLabel);
          }
        });
        this.slotClosed.set(slotClosed);
      }
    });
  }

  ngOnInit(): void {
    const storeUuid = this.data.store.uuid;
    if (!storeUuid) {
      this.isTimeslotsError.set(true);
    } else {
      this.store.set({
        uuid: this.data.store.uuid ?? '',
        name: this.data.store.name ?? '',
        phone: formatPhone(this.data.store.phone ?? ''),
        streetName: this.data.store.streetName ?? '',
        city: this.data.store.city ?? '',
        zipcode: this.data.store.zipcode ?? '',
        isVisioAppointmentAvailable: this.data.store.isVisioAppointmentAvailable ?? false,
      });
      this.timeslotsService
        .getTimeslots(storeUuid)
        .pipe(
          take(1),
          tap((value) => {
            this.logs.debug('AgendaMagasinComponent', 'Successfully fetched timeslots', value);
            if ('title' in value) {
              this.isTimeslotsError.set(true);
            } else {
              const hasAvailableSlot = value.some((slot) => (slot.formattedSlots ?? []).length > 0);
              if (hasAvailableSlot) {
                this.logs.debug('AgendaMagasinComponent', 'Slots are available and will be displayed.');
                this.timeslots.set(value.slice(0, 4));
              } else {
                this.logs.debug('AgendaMagasinComponent', 'No available slots for the selected store.');
                this.isTimeslotsError.set(true);
              }
            }
          }),
          catchError((e) => {
            this.logs.error('AgendaMagasinComponent', 'An error occurred while requesting the timeslots', e);
            this.isTimeslotsError.set(true);
            return EMPTY;
          })
        )
        .subscribe();
      this.elasticSearchService
        .getStoreSchedule(storeUuid)
        .pipe(
          take(1),
          tap((schedule) => {
            // A store about to open (OPENING_SOON) keeps its slots: appointments are taken before opening day.
            if (schedule.operationalStatus?.code === 'TEMPORARILY_CLOSED') this.isTimeslotsError.set(true);
          }),
          catchError((e) => {
            this.logs.error('AgendaMagasinComponent', 'getStoreSchedule failed', e);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  toggleSlot(dateLabel: string): void {
    const slotClosed = this.slotClosed();
    if (slotClosed.has(dateLabel)) {
      slotClosed.delete(dateLabel);
    } else {
      slotClosed.add(dateLabel);
    }
    this.slotClosed.set(slotClosed);
  }

  onSlotClick(date: Timeslots, formattedSlots: FormattedSlots): void {
    this.funnelStore.batchUpdate({
      update: {
        storeSelected: this.store() as StoreWithUuid,
        slotSelected: {
          dateLabel: date.dateLabel,
          formattedSlots,
          visioAppointmentSelected: this.visioAppointmentSelected(),
        },
        validatedStep: 2,
      },
    });
    void this.router.navigate([this.appointmentFunnel()?.urls.step3]);
  }

  goToFunnel(): void {
    this.funnelStore.batchUpdate({
      update: {
        storeSelected: this.store() as StoreWithUuid,
        validatedStep: 1,
      },
      delete: ['slotSelected'],
    });
    void this.router.navigate([this.appointmentFunnel()?.urls.step2]);
  }

  hasMoreSlots(dateSlot: Timeslots): boolean {
    const limit = this.getSlotCount(dateSlot);
    return dateSlot.formattedSlots.length > limit;
  }

  showMoreSlots(dateSlot: Timeslots): void {
    const current = this.getSlotCount(dateSlot);
    this.displayCountMap.set(dateSlot, current + N_DISPLAYED_SLOTS);
  }

  getSlotCount(dateSlot: Timeslots) {
    return this.displayCountMap.get(dateSlot) ?? N_DISPLAYED_SLOTS;
  }
}
